import type {AppealItem, TraderItem} from "../../api";
import type {ResolveDisputeOutcome} from "../../api";

/**
 * Решения разбора спора, которые можно проверить без рендера.
 *
 * Вынесены из `DisputeDetails` не ради красоты: карточка тянет за собой
 * контекст авторизации, модалку и загрузку файлов, и правила, живущие внутри
 * неё, проверялись бы только глазами. Это денежный экран — тот, где админ
 * выбирает, кому начислить деньги.
 */

/** Трейдер в выпадающем списке: id для запроса и подпись для человека. */
export interface TraderOption {
    id: string;
    label: string;
}

/**
 * Список трейдеров для формы разбора: обычный, без поиска, иерархии и
 * группировки по командам — трейдеров единицы (В-34).
 *
 * Неактивные отсеиваются здесь, а не «предупреждением при выборе»: сервер
 * отбирает платёж по тому же условию и на неактивного ответит отказом. Форма,
 * предлагающая выбор, на который сервер откажет, — это отказ после нажатия, а
 * не подсказка.
 *
 * Команда стоит в подписи, потому что начисление идёт ей: админ выбирает
 * человека, а деньги получает его команда, и увидеть это он должен до нажатия.
 */
export function activeTraderOptions(items: TraderItem[]): TraderOption[] {
    return items
        .filter((item) => item.isActive)
        .map((item) => ({
            id: item.id,
            label: item.teamName ? `${item.name} — ${item.teamName}` : item.name,
        }));
}

/**
 * Отказ формы, когда трейдер не выбран. Пустая строка — не «значение по
 * умолчанию»: платёж принадлежит конкретному трейдеру, и «ничьих» начислений в
 * системе нет (В-35).
 *
 * Спрашивается только у заявки без держателя. У заявки с живым платежом трейдер
 * уже есть, и переназначать его закрытием нельзя: закрывается ЕГО платёж.
 */
export function disputeTraderChoiceError(unheld: boolean, traderId: string): string | null {
    if (!unheld) return null;
    if (traderId.trim() !== "") return null;
    return "Выберите трейдера, за которым числятся деньги — начисление пойдёт ему и его команде.";
}

/**
 * Базовый отказ формы, когда сумма закрытия не равна остатку заявки.
 *
 * Формулировка называет само правило, а не его частный случай: частичного
 * закрытия нет ни у кого, трейдер закрывает заявку одним платежом на полную
 * сумму (UOF-02, решение владельца 19.08.2026). В панели трейдера то же правило
 * исполнено полем `readOnly`, и здесь оно обязано звучать так же.
 */
const WHOLE_REMAINDER_ERROR = "Спор закрывается одним платежом на весь остаток.";

/**
 * Дописка к отказу для заявки, которую площадка уже закрыла у себя.
 *
 * Именно этот случай и приводит админа к отказу: площадка пересчитала заявку и
 * заплатила кабинету меньше, чем стоит заявка у нас, а изменить сумму заявки
 * нельзя и не будет можно. Отказ без этой фразы правдив, но бесполезен — он
 * запрещает, не называя того, что делать вместо, и админ уходит искать
 * несуществующую правку суммы. Путь ниже — единственный законный, и он же
 * закрыл инцидент 03.09.2026.
 */
const PLATFORM_CLOSED_WAY_OUT =
    " Площадка уже закрыла эту заявку у себя: если фактически заплачено меньше, закройте спор в неуспех" +
    " и разнесите выплату в карточке мерчанта, раздел «Заявки, закрытые площадкой».";

/**
 * Обычный OUT-спор с существующим платежом можно окончательно закрыть по
 * меньшему факту. Остальные сценарии сохраняют правило всего остатка.
 * null — сумма допустима для данного сценария.
 *
 * Ни `maxPayments`, ни `paymentsMade` в правиле не участвуют намеренно. Прежнее
 * условие смотрело на них (`paymentsMade + 1 >= maxPayments`) и означало другое
 * правило — «последний платёж закрывает остаток», лимит дробления. У заявки
 * кабинета `maxPayments = 0` («дробить можно сколько угодно»), и условие
 * вырождалось в истину — верный ответ по случайности, при неверном объяснении.
 * Зато у мерчанта с `maxPayments = 3` и нулём проведённых платежей оно было
 * ложным, и форма пропускала частичное закрытие — прямо против UOF-02.
 *
 * `externalFinal` — карточка заявки у площадки уже закрыта (`DisputeDetails`).
 * Он не меняет решения, только дописывает к отказу путь для внешней заявки.
 */
export function disputeSuccessAmountError(
    item: AppealItem,
    fiatAmountReal: number,
    externalFinal: boolean,
): string | null {
    if (item.payinParts) {
        const parts = item.payinParts.parts;
        if (parts.some((p) => !p.confirmedAt && !p.failureId)) return "По каждой части нужно подтвердить поступление или получить доказательство отказа.";
        const confirmed = parts.reduce((sum, p) => sum + (p.confirmedAt ? p.amount : 0), 0);
        return confirmed > 0 && fiatAmountReal === confirmed ? null : "Сумма факта должна совпадать с подтверждёнными частями.";
    }
    if (fiatAmountReal === item.amountRemaining) return null;
    // An ordinary held payout may be closed for the actual amount paid. The
    // existing payment remains the one being resolved; an unheld order would
    // create a new payment, so it keeps the whole-remainder rule.
    if (
        item.workflowKind === "standard_payout" &&
        !item.externalSource &&
        !item.exchange &&
        item.paymentId !== null &&
        fiatAmountReal > 0 &&
        fiatAmountReal < item.amountRemaining
    ) return null;
    return externalFinal ? WHOLE_REMAINDER_ERROR + PLATFORM_CLOSED_WAY_OUT : WHOLE_REMAINDER_ERROR;
}

export function disputeFailureError(item: AppealItem): string | null {
    if (item.paymentsMade > 0 || item.payinParts?.parts.some((part) => part.confirmedAt)) {
        return "Частично оплачено — закрыть в неуспех нельзя.";
    }
    if (item.payinParts && (item.payinParts.parts.length === 0 || item.payinParts.parts.some((part) => !part.failureId || part.receiptIds.length > 0))) {
        return "Для закрытия в неуспех нужно доказательство отказа по каждой части без чеков оплаты.";
    }
    return null;
}

/**
 * В-32: кнопка «вернуть в пул» у админа СКРЫТА, а не удалена. Ручка,
 * исход `return_to_pool` и вся его логика остаются нетронутыми — возможно,
 * вернётся в более сложном виде.
 *
 * Поэтому здесь класс, а не `false` в условии рендера: карточка остаётся в
 * разметке со своим обработчиком, и включить её обратно — это одна строка CSS,
 * а не восстановление удалённого кода.
 */
export function resolutionCardClass(outcome: ResolveDisputeOutcome, extra?: string): string {
    const classes = ["resolution-card"];
    if (extra) classes.push(extra);
    if (outcome === "return_to_pool") classes.push("resolution-card-withdrawn");
    return classes.join(" ");
}

/**
 * Что уже сделал клиент по заявке-обмену, готовой фразой; null — сигналов нет
 * либо заявка не обменная.
 *
 * Ради этой фразы поле `exchange` и приехало в карточку спора. Бэкенд
 * «отрицательные» исходы по такой заявке не запрещает и запрещать не будет:
 * гейты сигналов стоят на трейдерских путях, а разбор спора по построению
 * остаётся человеку. Значит, единственное, что отделяет админа от необратимой
 * ошибки, — то, что он прочитает ДО нажатия.
 */
export function exchangeClientSignalsSummary(item: AppealItem): string | null {
    const exchange = item.exchange;
    if (!exchange) return null;
    const parts: string[] = [];
    if (exchange.clientMarkedPaidAt) parts.push("Клиент отметил оплату");
    if (exchange.clientReceiptCount > 0) parts.push(`приложил ${exchange.clientReceiptCount} чек(ов)`);
    if (parts.length === 0) return null;
    return `${parts.join(", ")}.`;
}

/**
 * Предупреждение для окна подтверждения «отрицательного» исхода по
 * заявке-обмену. null — предупреждать не о чем: заявка другого сценария либо
 * клиент по ней ещё ничего не сделал.
 *
 * Образец тот же, что у заявки кабинета: сначала факт, потом последствие, потом
 * слово «необратимо». Разница в том, что там необратимость чужой площадки, а
 * здесь — деньги клиента, которые уже ушли и которым после этого решения не к
 * кому приехать.
 */
export function exchangeOutcomeWarning(
    item: AppealItem,
    outcome: Exclude<ResolveDisputeOutcome, "success">,
): string | null {
    const summary = exchangeClientSignalsSummary(item);
    if (!summary) return null;
    const consequence = outcome === "return_to_pool"
        ? "Заявка уйдёт в пул к другому трейдеру"
        : "Заявка будет отменена";
    return `${summary} ${consequence}, деньги клиента останутся без адресата — решение необратимо.`;
}
